import { parseQty, allowsDecimalQty } from "./cartCalculations";
import {
  validateMaximumOrderQuantity,
  getMaximumOrderQuantityFromItem,
} from "../../common/maximumOrderQuantity";
import {
  getStockCapForCartLineItem,
  isQuantityWithinLineStock,
} from "../../Promotion/utils/promotionLineStock";
import {
  cartLinesShareBuyQtyValidationBucket,
  isCartLineGetOrFreeRole,
} from "../../Promotion/utils/promotionOfferProductsShared";

export const applyProductStockToCartLine = (line, stockFields) => {
  if (!line || !stockFields) return line;
  return {
    ...line,
    availableBoxQty: stockFields.availableBoxQty,
    availablePieceQty: stockFields.availablePieceQty,
    ...(stockFields.stock !== undefined && stockFields.stock !== null
      ? { stock: stockFields.stock }
      : {}),
  };
};

export const validateCartStockBeforePlaceOrder = (
  cartItems,
  stockByProductId,
  options = {}
) => {
  const {
    shouldCheckStock = true,
    shouldSkipStockCheck = () => false,
  } = options;
  if (!shouldCheckStock || !Array.isArray(cartItems) || cartItems.length === 0) {
    return { ok: true };
  }
  for (let i = 0; i < cartItems.length; i += 1) {
    const item = cartItems[i];
    if (!item || shouldSkipStockCheck(item) || isCartLineGetOrFreeRole(item)) {
      continue;
    }
    const pid = String(item.product_id ?? item.productId ?? "");
    const stockFields =
      stockByProductId instanceof Map
        ? stockByProductId.get(pid)
        : stockByProductId?.[pid];
    const lineForCheck = stockFields
      ? applyProductStockToCartLine(item, stockFields)
      : item;
    const allowDecimal = allowsDecimalQty(item);
    const lineKey = getCartLineValidationKey(item);
    const lineQty = parseQty(item, pickQtyRaw(item, "qty"));
    const totalQty = getProductQtyTotalInCart(
      cartItems,
      item,
      "qty",
      lineKey,
      lineQty
    );
    if (!isQuantityWithinLineStock(lineForCheck, totalQty, allowDecimal)) {
      const cap = getStockCapForCartLineItem(lineForCheck);
      return {
        ok: false,
        item,
        cap:
          cap != null
            ? allowDecimal
              ? cap
              : Math.floor(cap)
            : 0,
        productName:
          item.product_name ||
          item.name ||
          item.desc ||
          item.productName ||
          "Product",
        productCode: item.product_code || item.code || "",
      };
    }
  }
  return { ok: true };
};

export const getCartLineValidationKey = (item) => {
  if (!item) return "";
  const id = item.id ?? item.product_id ?? item.productId ?? "";
  const orderType = String(item.order_type || item.type || "Box");
  const promoType = String(
    item.promo_type ?? item.promoType ?? item.promosType ?? ""
  )
    .trim()
    .toUpperCase();
  return `${id}_${orderType}_${promoType}`;
};

const pickQtyRaw = (item, qtyField) => {
  if (qtyField === "allot") {
    return (
      item.allot_qty ??
      item.accept_qty ??
      item.qty ??
      item.order_qty ??
      item.quantity
    );
  }
  return item.qty ?? item.quantity ?? item.order_qty ?? item.allot_qty;
};

export const syncCartQtyValidationMap = (cartItems, options = {}) => {
  const qtyField = options.qtyField || "qty";
  const map = new Map();
  if (!Array.isArray(cartItems)) return map;
  cartItems.forEach((item) => {
    if (!item) return;
    map.set(getCartLineValidationKey(item), parseQty(item, pickQtyRaw(item, qtyField)));
  });
  return map;
};

export const getValidatedBaseQty = (validationMap, item, qtyField = "qty") => {
  const key = getCartLineValidationKey(item);
  if (validationMap.has(key)) {
    return validationMap.get(key);
  }
  return parseQty(item, pickQtyRaw(item, qtyField));
};

export const getProductQtyTotalInCart = (
  cartItems,
  sourceItem,
  qtyField,
  lineKey,
  lineQty
) => {
  if (!Array.isArray(cartItems) || !sourceItem) {
    return lineQty;
  }
  const pid = String(sourceItem.product_id ?? sourceItem.productId ?? "");
  if (!pid) {
    return lineQty;
  }
  return cartItems.reduce((sum, line) => {
    if (!line || String(line.product_id ?? line.productId ?? "") !== pid) {
      return sum;
    }
    if (!cartLinesShareBuyQtyValidationBucket(line, sourceItem)) {
      return sum;
    }
    const key = getCartLineValidationKey(line);
    const q =
      key === lineKey
        ? lineQty
        : parseQty(line, pickQtyRaw(line, qtyField));
    return sum + (Number.isFinite(q) ? q : 0);
  }, 0);
};

export const getQtyControlMax = (item, qtyField = "qty", cartItems = null) => {
  const allowDecimal = allowsDecimalQty(item);
  const globalMax = allowDecimal ? 9999.99 : 9999;
  let capMax = globalMax;
  const stockCap = getStockCapForCartLineItem(item);
  if (stockCap != null && Number.isFinite(stockCap)) {
    let effectiveStockCap = stockCap;
    if (Array.isArray(cartItems) && cartItems.length > 0) {
      const lineKey = getCartLineValidationKey(item);
      const othersTotal = getProductQtyTotalInCart(
        cartItems,
        item,
        qtyField,
        lineKey,
        0
      );
      effectiveStockCap = Math.max(0, stockCap - othersTotal);
    }
    capMax = Math.min(
      capMax,
      Math.max(
        0,
        allowDecimal ? effectiveStockCap : Math.floor(effectiveStockCap)
      )
    );
  }
  const purchaseCap = getMaximumOrderQuantityFromItem(item);
  if (purchaseCap != null && purchaseCap > 0) {
    let effectivePurchaseCap = purchaseCap;
    if (Array.isArray(cartItems) && cartItems.length > 0) {
      const lineKey = getCartLineValidationKey(item);
      const othersTotal = getProductQtyTotalInCart(
        cartItems,
        item,
        qtyField,
        lineKey,
        0
      );
      effectivePurchaseCap = Math.max(0, purchaseCap - othersTotal);
    }
    capMax = Math.min(
      capMax,
      Math.max(
        0,
        allowDecimal ? effectivePurchaseCap : Math.floor(effectivePurchaseCap)
      )
    );
  }
  return capMax;
};

// Batched counterpart to getQtyControlMax: groups cartItems once (O(n)) into small
// product+role buckets (the same bucketing getProductQtyTotalInCart already applies via
// product_id + cartLinesShareBuyQtyValidationBucket), so each row can call the existing,
// unmodified getQtyControlMax/getProductQtyTotalInCart against just its own bucket instead of
// the whole cart - same result, far less rescanning when a cart has many distinct products.
export const buildQtyControlMaxBuckets = (cartItems) => {
  const buckets = new Map();
  if (Array.isArray(cartItems)) {
    for (const line of cartItems) {
      if (!line) continue;
      const pid = String(line.product_id ?? line.productId ?? "");
      if (!pid) continue;
      const bucketKey = `${pid}|${isCartLineGetOrFreeRole(line) ? "1" : "0"}`;
      if (!buckets.has(bucketKey)) {
        buckets.set(bucketKey, []);
      }
      buckets.get(bucketKey).push(line);
    }
  }
  return buckets;
};

export const getQtyControlBucketItems = (item, buckets = null) => {
  if (Array.isArray(buckets)) return buckets;
  if (!buckets || typeof buckets.get !== "function") return null;
  const pid = String(item?.product_id ?? item?.productId ?? "");
  const bucketKey = `${pid}|${isCartLineGetOrFreeRole(item) ? "1" : "0"}`;
  return buckets.get(bucketKey) || null;
};

export const getQtyControlMaxFromBuckets = (item, qtyField = "qty", buckets = null) => {
  if (!buckets) {
    return getQtyControlMax(item, qtyField, null);
  }
  return getQtyControlMax(item, qtyField, getQtyControlBucketItems(item, buckets));
};

export const computeCartQuantityChange = ({
  sourceItem,
  validationMap,
  value,
  changeType = "absolute",
  qtyField = "qty",
  shouldCheckStockBeforeAddToCart = false,
  shouldSkipStockCheck = () => false,
  cartItems = null,
  forceAllowDecimal = false,
}) => {
  const allowDecimal = forceAllowDecimal || allowsDecimalQty(sourceItem);
  const minQty =
    qtyField === "allot"
      ? allowDecimal
        ? 0.01
        : 0
      : allowDecimal
        ? 0.01
        : 1;
  const maxQty = allowDecimal ? 9999.99 : 9999;
  const lineKey = getCartLineValidationKey(sourceItem);
  const baseQty = getValidatedBaseQty(validationMap, sourceItem, qtyField);
  let finalQty;

  if (changeType === "increment" || changeType === "decrement") {
    finalQty = baseQty + value;
  } else {
    const strVal = String(value);
    const hasDecimal = strVal.includes(".");
    finalQty = hasDecimal ? parseFloat(strVal) : parseInt(strVal, 10);
  }

  if (!Number.isFinite(finalQty)) {
    return { ok: false, reason: "invalid" };
  }

  if (allowDecimal) {
    if (finalQty < minQty || finalQty > maxQty) {
      return { ok: false, reason: "range", minQty, maxQty };
    }
  } else if (!Number.isInteger(finalQty) || finalQty < minQty || finalQty > maxQty) {
    return { ok: false, reason: "range", minQty, maxQty };
  }

  const purchaseQtyToCheck = Array.isArray(cartItems)
    ? getProductQtyTotalInCart(
        cartItems,
        sourceItem,
        qtyField,
        lineKey,
        finalQty
      )
    : finalQty;
  const purchaseLimit = validateMaximumOrderQuantity(
    sourceItem,
    purchaseQtyToCheck
  );
  if (!purchaseLimit.allowed) {
    return {
      ok: false,
      reason: "purchase_limit",
      message: purchaseLimit.message,
    };
  }

  if (shouldCheckStockBeforeAddToCart && !shouldSkipStockCheck(sourceItem)) {
    const stockQtyToCheck = Array.isArray(cartItems)
      ? getProductQtyTotalInCart(
          cartItems,
          sourceItem,
          qtyField,
          lineKey,
          finalQty
        )
      : finalQty;
    if (!isQuantityWithinLineStock(sourceItem, stockQtyToCheck, allowDecimal)) {
      const cap = getStockCapForCartLineItem(sourceItem);
      return {
        ok: false,
        reason: "stock",
        cap: cap != null ? Math.floor(cap) : 0,
      };
    }
  }

  return { ok: true, finalQty, lineKey };
};

export const applyCartQtyValidationResult = (result, handlers = {}) => {
  if (result.ok) {
    return true;
  }
  const { onRange, onPurchaseLimit, onStock } = handlers;
  if (result.reason === "range" && onRange) {
    onRange(result);
  } else if (result.reason === "purchase_limit" && onPurchaseLimit) {
    onPurchaseLimit(result);
  } else if (result.reason === "stock" && onStock) {
    onStock(result);
  }
  return false;
};
